import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewQueryRecordNodeWithId,
} from '@salescore/core'
import { useScroll } from '@salescore/frontend-common'
import { Array, Equivalence, Option, pipe, Record } from 'effect'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTimeoutFn } from 'react-use'

import type { RSheetColumn, RSheetRecordNode } from '../../domain/column'
import { useCalculatedRowHeight } from '../../recoil/navigation/hooks'
import { useGetRowHeight } from '../recoil/selectors/rowsRelatedSelector'

/** スクロールした時、スクロール方向へ事前に描画しておく領域幅 */
const RENDER_AHEAD_BUFFER_PX = 30
const IMMEDIATE_UPDATE_BUFFER_THRESHOLD_PX = 20

interface Position {
  x: number
  y: number
}

interface ScrollDelta {
  dx: number
  dy: number
}

type PixelArea = Record<'topLeft' | 'bottomRight', Position>

type RenderRangeIndices = Record<'left' | 'right' | 'top' | 'bottom', number>

const isSameRenderRangeIndices = Record.makeEquivalence<keyof RenderRangeIndices, number>(Equivalence.Number)

/**
 * シートの表示範囲に入る行・列だけを描画するため、描画する行・列の範囲を求める
 * @param scrollContainerElement シートのスクロール領域。ローディングのたびに作り直されるため、作り直しで再描画されるよう state に持った要素を渡す (描画中に読んだ ref.current では作り直しを検知できない)
 */
export const useVirtualizedBody = (
  scrollContainerElement: HTMLDivElement | null,
  rows: ViewQueryRecordNodeWithId[],
  notFixedColumns: Array<RSheetColumn<RSheetRecordNode>>,
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  visibleRowNumber: number | undefined,
): {
  renderingRowsTop: number
  renderingRowsTopIndex: number
  renderingRowsBottomIndex: number
  renderingRowsBottom: number
  renderingColumnsLeftIndex: number
  renderingColumnsLeft: number
  renderingColumnsRightIndex: number
  // eslint-disable-next-line @typescript-eslint/max-params
} => {
  const { getRowHeight } = useGetRowHeight(visibleRowNumber) // 子レコードを合わせた行の高さ
  const calculatedRowHeight = useCalculatedRowHeight(compiledConfig) // 1 行あたりの高さ
  // Array.scanで得る配列の先頭は初期値が入る。lengthがscanの引数の配列より1だけ大きい配列を得る。
  const cumulativeHeights = useMemo(
    () =>
      pipe(
        rows,
        Array.map((row) => getRowHeight(row) * calculatedRowHeight),
        Array.scan(0, (accumulator, height) => accumulator + height),
      ),
    [calculatedRowHeight, getRowHeight, rows],
  )
  const cumulativeWidths = useMemo(
    () =>
      pipe(
        notFixedColumns,
        Array.map(({ width }) => width ?? 300),
        Array.scan(0, (accumulator, width) => accumulator + width),
      ),
    [notFixedColumns],
  )
  const cumulativePixelOffsets = useMemo(
    () => ({ heights: cumulativeHeights, widths: cumulativeWidths }),
    [cumulativeHeights, cumulativeWidths],
  )

  const [renderRangeIndices, setRenderRangeIndices] = useState({
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  })
  const renderedPixelArea = useMemo(
    () => ({
      topLeft: {
        x: Array.get(cumulativeWidths, renderRangeIndices.left).pipe(Option.getOrElse(() => 0)),
        y: Array.get(cumulativeHeights, renderRangeIndices.top).pipe(Option.getOrElse(() => 0)),
      },
      bottomRight: {
        x: Array.get(cumulativeWidths, renderRangeIndices.right).pipe(Option.getOrElse(() => 0)),
        y: Array.get(cumulativeHeights, renderRangeIndices.bottom).pipe(Option.getOrElse(() => -1)),
      },
    }),
    [cumulativeHeights, cumulativeWidths, renderRangeIndices],
  )
  const pendingRenderRangeIndicesReference = useRef(renderRangeIndices)
  // scheduleRenderRangeIndicesUpdate を呼ぶたびに 500ms を数え直すので、スクロールし続けている間は 500ms 後の更新が先送りされ、止まってから 1 回だけ実行される
  const [, cancelScheduledRenderRangeUpdate, scheduleRenderRangeIndicesUpdate] = useTimeoutFn(() => {
    setRenderRangeIndices(pendingRenderRangeIndicesReference.current)
  }, 500)

  /** scroll イベントごとにスクロールした向きを求めるため、直前のスクロール位置を持っておく */
  const scrollPositionReference = useRef({ x: 0, y: 0 })

  // スクロール領域が付いたとき・行や列が変わったとき・スクロール領域の大きさが変わったときに、RSheet で描画する行・列の範囲を求め直す
  useEffect(() => {
    if (scrollContainerElement === null) return

    /**
     * スクロール位置と表示領域の大きさを DOM から読み、RSheet で描画する行・列の範囲を求め直す
     * - ローディングで作り直されたスクロール領域は scroll イベントを出さず、記録が古いまま残るため、
     *   scroll イベントで記録したスクロール位置は使わない
     */
    const recalcRenderRangeIndices = (): void => {
      // 記録しているスクロール位置も DOM の値に更新し、次の scroll イベントでスクロールした向きを正しく求められるようにする
      scrollPositionReference.current = { x: scrollContainerElement.scrollLeft, y: scrollContainerElement.scrollTop }
      const viewportArea = toViewportArea(scrollPositionReference.current, scrollContainerElement)
      const nextRenderRangeIndices = calcRenderRangeIndices(viewportArea, cumulativePixelOffsets)
      pendingRenderRangeIndicesReference.current = nextRenderRangeIndices
      // ResizeObserver は監視を付けるたびに通知するため、範囲が変わらないときは再描画しない
      setRenderRangeIndices((current) =>
        isSameRenderRangeIndices(current, nextRenderRangeIndices) ? current : nextRenderRangeIndices,
      )
    }

    recalcRenderRangeIndices()
    const resizeObserver = new ResizeObserver(recalcRenderRangeIndices)
    resizeObserver.observe(scrollContainerElement)
    return (): void => {
      resizeObserver.disconnect()
    }
  }, [cumulativePixelOffsets, scrollContainerElement])

  /** スクロールに合わせて、RSheet で描画する行・列の範囲を更新する */
  const onScroll = useCallback(
    (currentScrollPosition: Position) => {
      if (scrollContainerElement === null) return

      const previousScrollPosition = scrollPositionReference.current
      scrollPositionReference.current = currentScrollPosition
      const scrollDelta = {
        dx: currentScrollPosition.x - previousScrollPosition.x,
        dy: currentScrollPosition.y - previousScrollPosition.y,
      }
      if (scrollDelta.dx === 0 && scrollDelta.dy === 0) return

      const viewportArea = toViewportArea(currentScrollPosition, scrollContainerElement)

      const prefetchArea = extendAreaTowardScroll(viewportArea, scrollDelta)
      pendingRenderRangeIndicesReference.current = calcRenderRangeIndices(prefetchArea, cumulativePixelOffsets)
      // スクロール後に必要な行・列がすべて描画済みなら、描画し直さない
      if (
        renderRangeIndices.left <= pendingRenderRangeIndicesReference.current.left &&
        pendingRenderRangeIndicesReference.current.right <= renderRangeIndices.right &&
        renderRangeIndices.top <= pendingRenderRangeIndicesReference.current.top &&
        pendingRenderRangeIndicesReference.current.bottom <= renderRangeIndices.bottom
      )
        return

      const distanceToRenderedEdgePx = calcDistanceToRenderedEdgePx(viewportArea, renderedPixelArea, scrollDelta)
      // スクロールした先に描画済みの行・列がほとんど残っていないなら、空白が見えないよう 500ms 待たずに更新する
      if (distanceToRenderedEdgePx < IMMEDIATE_UPDATE_BUFFER_THRESHOLD_PX) {
        cancelScheduledRenderRangeUpdate()
        setRenderRangeIndices(pendingRenderRangeIndicesReference.current)
        return
      }
      // 早期 return にならなかった場合は 500ms 後にまとめて更新する
      scheduleRenderRangeIndicesUpdate()
    },
    [
      cancelScheduledRenderRangeUpdate,
      cumulativePixelOffsets,
      renderRangeIndices,
      scheduleRenderRangeIndicesUpdate,
      renderedPixelArea,
      scrollContainerElement,
    ],
  )

  // useScroll は受け取った RefObject が変わったときに scroll イベントの監視を付け直すため、作り直されたスクロール領域ごとに RefObject を作る
  const scrollContainerReference = useMemo(() => ({ current: scrollContainerElement }), [scrollContainerElement])
  useScroll(scrollContainerReference, onScroll)

  const bodyHeight = Array.lastNonEmpty(cumulativeHeights)

  return {
    renderingRowsTop: Array.get(cumulativeHeights, renderRangeIndices.top).pipe(Option.getOrElse(() => 0)),
    renderingRowsTopIndex: renderRangeIndices.top,
    renderingRowsBottomIndex: renderRangeIndices.bottom,
    renderingRowsBottom:
      bodyHeight - Array.get(cumulativeHeights, renderRangeIndices.bottom).pipe(Option.getOrElse(() => 0)),
    renderingColumnsLeftIndex: renderRangeIndices.left,
    renderingColumnsLeft: Array.get(cumulativeWidths, renderRangeIndices.left).pipe(Option.getOrElse(() => 0)),
    renderingColumnsRightIndex: renderRangeIndices.right,
  }
}

/** スクロール位置とスクロール領域の大きさから、シートの中で画面に映っている範囲を求める */
const toViewportArea = (topLeft: Position, root: HTMLDivElement): PixelArea => ({
  topLeft,
  bottomRight: { x: topLeft.x + root.clientWidth, y: topLeft.y + root.clientHeight },
})

/**
 * 表示範囲を、スクロールした向きへ RENDER_AHEAD_BUFFER_PX だけ広げる
 * - 縦横同時にスクロールしたときは、縦横それぞれの向きへ広げる
 */
const extendAreaTowardScroll = ({ topLeft, bottomRight }: PixelArea, { dx, dy }: ScrollDelta): PixelArea => ({
  topLeft: {
    x: dx < 0 ? topLeft.x - RENDER_AHEAD_BUFFER_PX : topLeft.x,
    y: dy < 0 ? topLeft.y - RENDER_AHEAD_BUFFER_PX : topLeft.y,
  },
  bottomRight: {
    x: dx > 0 ? bottomRight.x + RENDER_AHEAD_BUFFER_PX : bottomRight.x,
    y: dy > 0 ? bottomRight.y + RENDER_AHEAD_BUFFER_PX : bottomRight.y,
  },
})

/**
 * 表示範囲から、スクロールした向きにある描画済み部分の端までの長さを求める
 * - 縦横同時にスクロールしたときは、短いほうを返す
 */
const calcDistanceToRenderedEdgePx = (
  viewportArea: PixelArea,
  renderedPixelArea: PixelArea,
  { dx, dy }: ScrollDelta,
): number => {
  const distanceX = selectDistanceByScrollDirection(
    dx,
    viewportArea.topLeft.x - renderedPixelArea.topLeft.x,
    renderedPixelArea.bottomRight.x - viewportArea.bottomRight.x,
  )
  const distanceY = selectDistanceByScrollDirection(
    dy,
    viewportArea.topLeft.y - renderedPixelArea.topLeft.y,
    renderedPixelArea.bottomRight.y - viewportArea.bottomRight.y,
  )
  return Math.min(distanceX, distanceY)
}

/**
 * 1 つの軸について、スクロールした向きにある描画済み部分の端までの長さを選ぶ
 * @param delta その軸のスクロール量
 * @param distanceToStart 表示範囲の左端・上端から、描画済み部分の左端・上端までの長さ
 * @param distanceToEnd 表示範囲の右端・下端から、描画済み部分の右端・下端までの長さ
 * @returns その軸でスクロールしていないときは、もう一方の軸の長さが選ばれるよう Number.POSITIVE_INFINITY
 */
const selectDistanceByScrollDirection = (delta: number, distanceToStart: number, distanceToEnd: number): number => {
  if (delta < 0) return distanceToStart
  if (delta > 0) return distanceToEnd
  return Number.POSITIVE_INFINITY
}

/**
 * 引数の{@link PixelArea}の範囲に一部でも重なる行・列のインデックスを求める
 * @param { topLeft, bottomRight } 画面に映っている範囲、またはそれをスクロールした向きへ広げた範囲
 * @returns left・top は範囲に重なる最初のインデックス、right・bottom は範囲に重なる最後のインデックスの次 (Array.slice の終わりと同じく含まない)
 */
const calcRenderRangeIndices = (
  { topLeft, bottomRight }: PixelArea,
  cumulativePixelOffsets: Record<'heights' | 'widths', number[]>,
): RenderRangeIndices => ({
  left: calcLowerIndex(topLeft.x, cumulativePixelOffsets.widths),
  right: calcGreaterIndex(bottomRight.x, cumulativePixelOffsets.widths),
  top: calcLowerIndex(topLeft.y, cumulativePixelOffsets.heights),
  bottom: calcGreaterIndex(bottomRight.y, cumulativePixelOffsets.heights),
})

const calcLowerIndex = (position: number, cumulativeOffsets: number[]): number =>
  pipe(
    cumulativeOffsets,
    Array.findLastIndex((v) => v <= position),
    Option.getOrElse(() => 0),
  )

const calcGreaterIndex = (position: number, cumulativeOffsets: number[]): number =>
  pipe(
    cumulativeOffsets,
    Array.findFirstIndex((v) => position < v),
    Option.getOrElse(() => cumulativeOffsets.length - 1),
  )
